import { useState, useCallback, useMemo, type ReactNode } from 'react';
import styled from '@emotion/styled';

type LegendItem = {
  key: string;
  color: string;
  originalKey: string;
};

type ModalComponent = React.ComponentType<{
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}>;

type Props = {
  open: boolean;
  onClose: () => void;
  legendData: LegendItem[];
  colorOverrides: Record<string, string> | null;
  labelOverrides: Record<string, string> | null;
  insightsSection?: ReactNode;
  onSave: (colorOverrides: Record<string, string>, labelOverrides: Record<string, string>) => void;
  onReset?: () => void;
  ModalComponent: ModalComponent;
};

export const ChartKeyEditModal = ({
  open,
  onClose,
  legendData,
  colorOverrides,
  labelOverrides,
  insightsSection,
  onSave,
  onReset,
  ModalComponent
}: Props): React.JSX.Element => {
  const [editedColors, setEditedColors] = useState<Record<string, string>>(() => colorOverrides || {});
  const [editedLabels, setEditedLabels] = useState<Record<string, string>>(() => labelOverrides || {});
  const [hexInputValues, setHexInputValues] = useState<Record<string, string>>({});

  const handleLabelChange = useCallback((originalKey: string, newLabel: string) => {
    setEditedLabels(prev => ({ ...prev, [originalKey]: newLabel }));
  }, []);

  const handleLabelBlur = useCallback((originalKey: string) => {
    const currentLabel = editedLabels[originalKey];
    if (currentLabel !== undefined) {
      const trimmedLabel = currentLabel.trim();
      setEditedLabels(prev => ({ ...prev, [originalKey]: trimmedLabel }));
    }
  }, [editedLabels]);

  const handleHexInputChange = useCallback((originalKey: string, hexValue: string) => {
    setHexInputValues(prev => ({ ...prev, [originalKey]: hexValue }));
  }, []);

  const handleHexInputBlur = useCallback((originalKey: string) => {
    const hexValue = hexInputValues[originalKey];
    if (!hexValue) return;

    const cleanValue = hexValue.replace('#', '').toUpperCase();

    if (/^[0-9A-F]{6}$/i.test(cleanValue)) {
      const hexColor = `#${cleanValue}`;
      setEditedColors(prev => ({ ...prev, [originalKey]: hexColor }));
      setHexInputValues(prev => {
        const updated = { ...prev };
        delete updated[originalKey];
        return updated;
      });
    } else {
      setHexInputValues(prev => {
        const updated = { ...prev };
        delete updated[originalKey];
        return updated;
      });
    }
  }, [hexInputValues]);

  const handleSave = useCallback(() => {
    const hasInvalidLabels = Object.values(editedLabels).some(
      label => label !== undefined && label.trim().length === 0
    );
    if (hasInvalidLabels) return;
    onSave(editedColors, editedLabels);
    onClose();
  }, [editedColors, editedLabels, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setEditedColors(colorOverrides || {});
    setEditedLabels(labelOverrides || {});
    setHexInputValues({});
    onClose();
  }, [colorOverrides, labelOverrides, onClose]);

  // const handleReset = useCallback(() => {
  //   if (onReset) {
  //     setEditedColors({});
  //     setEditedLabels({});
  //     setHexInputValues({});
  //     onReset();
  //     onClose();
  //   }
  // }, [onReset, onClose]);

  const displayData = useMemo(() => {
    return legendData.map(item => ({
      ...item,
      displayColor: editedColors[item.originalKey] ?? item.color,
      displayLabel: editedLabels[item.originalKey] ?? item.key,
      hexInputValue: hexInputValues[item.originalKey] ?? (editedColors[item.originalKey] ?? item.color).replace('#', '').toUpperCase(),
    }));
  }, [legendData, editedColors, editedLabels, hexInputValues]);

  const canSave = !Object.values(editedLabels).some(
    label => label !== undefined && label.trim().length === 0
  );

  return (
    <ModalComponent open={open} onClose={handleCancel}>
      <ModalRoot>
        <ModalWrap>
          <ModalHeader>Edit Chart</ModalHeader>

          <ModalMain>
            <LegendContainer>
              <LegendScroll>
                <LegendWrap>
                  {displayData.map((item) => (
                    <LegendRow key={item.originalKey}>
                      <ColorInputWrap>
                        <ColorSwatch style={{ backgroundColor: item.displayColor }} />
                        <ColorInputMain>
                          <ColorInputPrefix>#</ColorInputPrefix>
                          <ColorInputField
                            type="text"
                            value={item.hexInputValue}
                            onChange={(e) => handleHexInputChange(item.originalKey, e.target.value)}
                            onBlur={() => handleHexInputBlur(item.originalKey)}
                            maxLength={6}
                          />
                        </ColorInputMain>
                      </ColorInputWrap>
                      <LabelInputWrap>
                        <LabelInput
                          type="text"
                          value={item.displayLabel}
                          onChange={(e) => handleLabelChange(item.originalKey, e.target.value)}
                          onBlur={() => handleLabelBlur(item.originalKey)}
                          placeholder="Enter label"
                        />
                      </LabelInputWrap>
                    </LegendRow>
                  ))}
                </LegendWrap>
              </LegendScroll>
            </LegendContainer>

            {insightsSection}

            <ButtonSet>
              <CancelButton onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SaveButton onClick={handleSave} disabled={!canSave}>
                Save
              </SaveButton>
            </ButtonSet>
          </ModalMain>
        </ModalWrap>
      </ModalRoot>
    </ModalComponent>
  );
};

ChartKeyEditModal.displayName = 'ChartKeyEditModal';

// Styled Components
const ModalRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  padding: '32px',
  boxSizing: 'border-box',
  width: '600px',
  maxWidth: '100vw',
});

const ModalWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100%',
  width: '100%',
});

const ModalMain = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '560px',
  minHeight: '500px',
  maxHeight: '80vh',
  padding: '0 20px',
  boxSizing: 'border-box',
});

const ModalHeader = styled('h2')({
  margin: '0 0 32px 0',
  fontFamily: 'var(--font-bold)',
  fontSize: '22px',
  color: 'var(--black, #1a1a1a)',
  textAlign: 'center',
});

const LegendContainer = styled('div')({
  flex: 1,
  marginBottom: '20px',
  overflow: 'hidden',
  minHeight: '300px',
});

const LegendScroll = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  overflowY: 'auto',
  height: '100%',
  paddingRight: '20px',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
});

const LegendWrap = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const LegendRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  marginBottom: '10px',
  gap: '20px',
});

const ColorInputWrap = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexShrink: 0,
});

const ColorSwatch = styled('div')({
  display: 'flex',
  height: '41px',
  width: '41px',
  borderRadius: '4px',
  flexShrink: 0,
});

const ColorInputMain = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 5px 10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'text',
  width: '120px',
  '&:focus-within': { borderColor: 'var(--gray-m, #999)' },
});

const ColorInputPrefix = styled('span')({
  fontSize: '16px',
  color: '#333',
});

const ColorInputField = styled('input')({
  fontFamily: 'var(--font-reg, inherit)',
  border: 'none',
  fontSize: '16px',
  width: '4.5em',
  paddingRight: '5px',
  textTransform: 'uppercase',
  background: 'transparent',
  '&:focus': { outline: 'none' },
  '&::selection': { backgroundColor: 'var(--blue-l, #b3d9ff)' },
});

const LabelInputWrap = styled('div')({
  flex: 1,
});

const LabelInput = styled('input')({
  width: '100%',
  padding: '10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  fontSize: '16px',
  fontFamily: 'var(--font-reg, inherit)',
  boxSizing: 'border-box',
  '&:focus': { outline: 'none', borderColor: 'var(--gray-m, #999)' },
  '&::placeholder': { color: 'var(--gray-l, #ccc)' },
});

const ButtonSet = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '45px',
  gap: '12px',
  flexShrink: 0,
  marginTop: 'auto',
  paddingTop: '20px',
});

const CancelButton = styled('button')({
  width: '150px',
  height: '100%',
  fontFamily: "var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  fontSize: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '5px',
  border: '1px solid var(--gray, #999)',
  color: 'var(--gray-d, #666)',
  backgroundColor: 'var(--pri-01, white)',
  transition: 'background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in',
  '&:hover': { backgroundColor: 'var(--gray-xl, #f5f5f5)' },
  '&:disabled': {
    borderColor: 'var(--gray-l, #ddd)',
    color: 'var(--pri-01, white)',
    backgroundColor: 'var(--gray-l, #ddd)',
    cursor: 'not-allowed',
  },
});

const SaveButton = styled('button')({
  width: '150px',
  height: '100%',
  fontFamily: "var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  fontSize: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '5px',
  border: '2px solid var(--pri-02, #4A90E2)',
  backgroundColor: 'var(--pri-02, #4A90E2)',
  color: 'var(--pri-01, white)',
  transition: 'background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in',
  '&:hover': { border: '2px solid var(--sec-02, #357ABD)', backgroundColor: 'var(--sec-02, #357ABD)' },
  '&:disabled': {
    borderColor: 'var(--gray-l, #ddd)',
    color: 'var(--pri-01, white)',
    backgroundColor: 'var(--gray-l, #ddd)',
    cursor: 'not-allowed',
  },
});
